/**
 * AuthProvider unificado.
 *
 * Consolida a lógica que vivia em apps/public/src/contexts/AuthContext.tsx
 * (5.8KB, 192 LOC) e apps/crm/src/contexts/AuthContext.tsx (12.1KB, 348 LOC),
 * resolvendo o CF-1 (Auth fork) catalogado em
 * pps/.claude/logs/audit-architecture-20260430-1558.md.
 *
 * Diferenças entre apps são expostas como features opt-in. Profile e Club
 * shapes são genéricos — cada app passa loaders tipados.
 *
 * Apps continuam responsáveis por:
 *   - i18n sync (depende do shape do profile do app)
 *   - Sentry breadcrumbs (passar onAuthChange)
 *   - viewScope/networkAdmin (escopo CRM-only, fica em apps/crm enquanto
 *     não houver consumer público; M-1 PR-c moverá para um sub-package
 *     se justificar)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import type { PpsSupabaseClient } from "./client";
import {
  signInWithPassword as authSignIn,
  signUpWithEmail as authSignUp,
  signOut as authSignOut,
} from "./lib/supabase-auth";
import { createBroadcastSignOutChannel, type BroadcastSignOutChannel } from "./lib/broadcast-channel";
import { registerVisibilityRehydrate } from "./lib/visibility-rehydrate";
import type {
  AuthContextValue,
  AuthProviderFeatures,
  MultiClubLoader,
  MultiClubState,
  OnAuthChange,
  ProfileLoader,
  SignInResult,
  SignOutScope,
  SignUpResult,
} from "./types";

// O contexto é tipado como `unknown` para permitir múltiplos providers
// com profile/club shapes diferentes coexistindo. O hook `useAuth<T,C>()`
// re-tipa via generics.
const AuthContext = createContext<AuthContextValue<unknown, unknown> | null>(null);

export interface AuthProviderProps<TProfile, TClub> {
  /** Cliente Supabase já criado via `createSupabaseClient`. Obrigatório. */
  supabaseClient: PpsSupabaseClient;
  /** Loader plugável de profile. Opcional — se ausente, profile fica null. */
  profileLoader?: ProfileLoader<TProfile>;
  /** Loader plugável multi-club. Obrigatório quando features.multiClub=true. */
  multiClubLoader?: MultiClubLoader<TClub>;
  /** Features opt-in. Veja types.ts para detalhes. */
  features?: AuthProviderFeatures;
  /** Callback disparado em todo onAuthStateChange (Sentry breadcrumbs etc). */
  onAuthChange?: OnAuthChange;
  children: ReactNode;
}

export function AuthProvider<TProfile = unknown, TClub = unknown>(
  props: AuthProviderProps<TProfile, TClub>
): ReactElement {
  const {
    supabaseClient,
    profileLoader,
    multiClubLoader,
    features,
    onAuthChange,
    children,
  } = props;

  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<TProfile | null>(null);
  const [clubs, setClubs] = useState<TClub[]>([]);
  const [club, setClub] = useState<TClub | null>(null);
  const [loading, setLoading] = useState(true);

  // Refs para evitar deps instáveis em useEffect.
  const intentionalSignOut = useRef(false);
  const profileLoaderRef = useRef(profileLoader);
  const multiClubLoaderRef = useRef(multiClubLoader);
  const onAuthChangeRef = useRef(onAuthChange);
  // Tracker para race conditions: se um sign-in dispara fetch e outro chega
  // antes do primeiro completar, descartamos o resultado obsoleto.
  const fetchTokenRef = useRef(0);
  // Channel singleton compartilhado entre publish (no signOut) e subscribe
  // (no useEffect). Criado uma vez por mount quando feature ligada.
  const broadcastChannelRef = useRef<BroadcastSignOutChannel | null>(null);

  profileLoaderRef.current = profileLoader;
  multiClubLoaderRef.current = multiClubLoader;
  onAuthChangeRef.current = onAuthChange;

  // ---------------------------------------------------------------------
  // Loader interno: carrega profile + multiClub para um userId, respeitando
  // o token de versão (descartar resultados obsoletos em caso de race).
  // ---------------------------------------------------------------------
  const loadUserData = useCallback(
    async (userId: string): Promise<void> => {
      const myToken = ++fetchTokenRef.current;
      try {
        const [profileResult, clubsResult] = await Promise.all([
          profileLoaderRef.current
            ? profileLoaderRef.current(supabaseClient, userId)
            : Promise.resolve(null),
          features?.multiClub && multiClubLoaderRef.current
            ? multiClubLoaderRef.current(supabaseClient, userId)
            : Promise.resolve({ clubs: [] as TClub[], initial: null as TClub | null }),
        ]);

        if (myToken !== fetchTokenRef.current) return; // resultado obsoleto

        setProfile(profileResult);
        setClubs(clubsResult.clubs);
        setClub(clubsResult.initial);
      } finally {
        if (myToken === fetchTokenRef.current) {
          setLoading(false);
        }
      }
    },
    [supabaseClient, features?.multiClub]
  );

  const clearUserData = useCallback((): void => {
    fetchTokenRef.current++;
    setProfile(null);
    setClubs([]);
    setClub(null);
  }, []);

  // ---------------------------------------------------------------------
  // Effect principal: subscribe ao auth state + carrega sessão inicial.
  // ---------------------------------------------------------------------
  useEffect(() => {
    let mounted = true;

    if (features?.broadcastSignOut && !broadcastChannelRef.current) {
      broadcastChannelRef.current = createBroadcastSignOutChannel(
        features.broadcastChannelName ?? "pps-auth"
      );
    }

    const unsubscribeBroadcast = broadcastChannelRef.current
      ? broadcastChannelRef.current.subscribe(() => {
          // Outra aba pediu sign-out: limpa estado local sem chamar API.
          intentionalSignOut.current = true;
          setSession(null);
          setUser(null);
          clearUserData();
          setLoading(false);
        })
      : () => {};

    /**
     * Aplica no React o estado de auth resolvido. Extraído do callback para
     * que o guard deferido (abaixo) chegue ao MESMO caminho quando conclui
     * que o sign-out era real — sem duplicar a transição de estado.
     */
    const applyAuthState = (
      event: AuthChangeEvent,
      nextSession: Session | null
    ): void => {
      if (!mounted) return;

      intentionalSignOut.current = false;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      onAuthChangeRef.current?.(event, nextSession);

      if (nextSession?.user) {
        // Defer fetch para fora do callback do listener — evita deadlock
        // documentado pelo Supabase no Auth state machine.
        queueMicrotask(() => {
          if (mounted) void loadUserData(nextSession.user.id);
        });
      } else {
        clearUserData();
        setLoading(false);
      }
    };

    // Callback SÍNCRONO de propósito: o GoTrue segura o lock
    // `lock:sb-<ref>-auth-token` enquanto aguarda este callback, e o Supabase
    // documenta não chamar outras funções de auth aqui dentro.
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!mounted) return;

        // Spurious SIGNED_OUT guard: ignora SIGNED_OUT que não foi disparado
        // por nós e ainda há sessão válida no storage. Defesa contra v2 quirk.
        if (
          features?.guardSpuriousSignOut &&
          event === "SIGNED_OUT" &&
          !intentionalSignOut.current
        ) {
          // O guard PRECISA consultar o storage, mas não pode fazer isso aqui:
          // `getSession()` dentro do callback reentra no lock que o GoTrue já
          // detém, e o segura pela duração da consulta. O sintoma em produção
          // não é um travamento visível — é o tick de auto-refresh do
          // gotrue-js perdendo o lock e virando unhandledrejection
          // ("Acquiring an exclusive Navigator LockManager lock ...
          // immediately failed", PLAYPROSPORT-10), e contenção no refresh que
          // o GoTrue pode ler como reuso de refresh token ("Possible abuse
          // attempt") e revogar a sessão — o logout inesperado.
          //
          // setTimeout e não queueMicrotask: microtask ainda roda na mesma
          // volta do event loop, antes de o lock ser liberado. A macrotask
          // garante que a consulta acontece com o lock já solto.
          //
          // Enquanto a decisão não volta, o estado ANTERIOR é mantido: se o
          // SIGNED_OUT for espúrio, o usuário nem pisca deslogado.
          setTimeout(() => {
            if (!mounted) return;
            supabaseClient.auth
              .getSession()
              .then(({ data }) => {
                if (!mounted) return;
                if (data.session) {
                  setSession(data.session);
                  setUser(data.session.user);
                  return;
                }
                applyAuthState(event, null);
              })
              .catch(() => {
                // getSession falhou também — segue com sign-out.
                applyAuthState(event, null);
              });
          }, 0);
          return;
        }

        applyAuthState(event, nextSession);
      }
    );

    // Sessão inicial
    supabaseClient.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (data.session?.user) {
        void loadUserData(data.session.user.id);
      } else {
        setLoading(false);
      }
    });

    const unsubscribeVisibility = features?.visibilityRehydrate
      ? registerVisibilityRehydrate({
          client: supabaseClient,
          onSession: (s) => {
            if (!mounted) return;
            if (s) {
              setSession(s);
              setUser(s.user);
            }
          },
        })
      : () => {};

    return () => {
      mounted = false;
      subscription.unsubscribe();
      unsubscribeBroadcast();
      unsubscribeVisibility();
      broadcastChannelRef.current?.close();
      broadcastChannelRef.current = null;
    };
    // Deps intencionalmente reduzidas: features são lidas via closure no
    // primeiro mount. Mudar features em runtime não é caso de uso suportado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseClient, loadUserData, clearUserData]);

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------
  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      return authSignIn(supabaseClient, email, password);
    },
    [supabaseClient]
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: { fullName?: string; redirectTo?: string }
    ): Promise<SignUpResult> => {
      return authSignUp(supabaseClient, email, password, metadata);
    },
    [supabaseClient]
  );

  const signOut = useCallback(async (options?: { scope?: SignOutScope }): Promise<void> => {
    intentionalSignOut.current = true;
    await authSignOut(supabaseClient, options?.scope ?? "local");
    // Publica APÓS signOut local (consumidores em outras abas podem
    // depender do storage estar limpo).
    broadcastChannelRef.current?.publish();
  }, [supabaseClient]);

  const refreshProfile = useCallback(async (): Promise<void> => {
    if (!user?.id || !profileLoaderRef.current) return;
    const next = await profileLoaderRef.current(supabaseClient, user.id);
    setProfile(next);
  }, [user?.id, supabaseClient]);

  const selectClub = useCallback((c: TClub): void => {
    setClub(c);
  }, []);

  const multiClubState: MultiClubState<TClub> | null = useMemo(() => {
    if (!features?.multiClub) return null;
    return { club, clubs, selectClub };
  }, [features?.multiClub, club, clubs, selectClub]);

  const value: AuthContextValue<TProfile, TClub> = useMemo(
    () => ({
      session,
      user,
      profile,
      loading,
      multiClub: multiClubState,
      signIn,
      signUp,
      signOut,
      refreshProfile,
    }),
    [session, user, profile, loading, multiClubState, signIn, signUp, signOut, refreshProfile]
  );

  return (
    <AuthContext.Provider value={value as AuthContextValue<unknown, unknown>}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook tipado. Apps passam os generics do seu domínio:
 *   const { profile } = useAuth<MyProfile>();
 *
 * Sem generics, profile/club ficam como `unknown` — o consumidor precisa
 * fazer narrowing.
 */
export function useAuth<TProfile = unknown, TClub = unknown>(): AuthContextValue<TProfile, TClub> {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx as AuthContextValue<TProfile, TClub>;
}
